<template>
  <div class="flex gap-1 p-1 border-b-2 rounded-t-md bg-grey-900">
    <PButton
      ref="removeBtn"
      @click="remove"
      size="icon"
      v-if="audioRecorder.state.value === 'recording' || audioRecorder.state.value === 'paused'"
      mode="secondary"
      aria-label="supprimer l'enregistrement"
      data-sentry-id="AudioRecorderRemoveBtn"
    >
      <OhVueIcon :name="deleteIcon" />
    </PButton>
    <PTooltip
      v-if="audioRecorder.state.value === 'recording'"
      label="Mettre en pause l'enregistrement"
      :delay="500"
      :offset="5"
      activator-wrapper-classes="h-full"
    >
      <PButton
        @click="pause"
        size="icon"
        mode="secondary"
        aria-label="Mettre l'enregistrement en pause"
        :color="pulse ? 'error' : 'primary'"
        class="h-full"
        data-sentry-id="AudioRecorderPauseBtn"
      >
        <OhVueIcon :name="recordIconFill" />
      </PButton>
    </PTooltip>
    <PTooltip
      v-if="audioRecorder.state.value === 'paused'"
      :label="
        audioRecorder.reachedMaxDuration.value
          ? 'Durée d\'enregistrement maximale atteinte'
          : 'Reprendre l\'enregistrement'
      "
      :delay="500"
      :offset="5"
    >
      <PButton
        :disabled="audioRecorder.reachedMaxDuration.value"
        @click="resume"
        size="icon"
        mode="secondary"
        color="error"
        :aria-label="
          audioRecorder.reachedMaxDuration.value
            ? 'Durée d\'enregistrement maximale atteinte'
            : 'Reprendre l\'enregistrement'
        "
        data-sentry-id="AudioRecorderResumeBtn"
      >
        <OhVueIcon :name="recordIconLine" />
      </PButton>
    </PTooltip>
    <!-- <PButton
      v-if="audioRecorder.state.value === 'paused' && false"
      @click="setupPlayback"
      mode="secondary"
      size="icon"
      aria-label="Jouer l'enregistrement"
    >
      <OhVueIcon :name="playIcon" />
    </PButton> -->
    <div
      v-if="audioRecorder.state.value === 'recording' || audioRecorder.state.value === 'paused'"
      class="w-full flex items-center"
    >
      <div v-show="!playerSrc" class="bg-grey-425 w-full rounded-md px-1v mx-1v">
        <div class="flex items-center">
          <div class="bg-blue-france-sun-113-active text-white px-1 rounded-md pb-[2px] mr-1">
            <span>
              {{ audioRecorder.recordTime.value }}
            </span>
          </div>
          <canvas class="w-full h-5" ref="canvas" />
        </div>
      </div>
      <AudioPlayerCustom v-if="playerSrc" :src="playerSrc" v-model="playedTime" class="h-3 border border-grey-200">
        <template #time="{ currentMinutes, currentSeconds, totalMinutes, totalSeconds }">
          <p class="px-1 text-sm self-center whitespace-nowrap text-grey-975">
            <span>{{ currentMinutes }}:{{ currentSeconds }}</span> /
            <span :class="{ 'text-error': audioRecorder.reachedMaxDuration.value }"
              >{{ totalMinutes }}:{{ totalSeconds }}</span
            >
          </p>
        </template>
      </AudioPlayerCustom>
    </div>
    <PButton
      @click="send"
      size="icon"
      v-if="audioRecorder.state.value === 'recording' || audioRecorder.state.value === 'paused'"
      aria-label="envoyer"
      data-sentry-id="AudioRecorderSendBtn"
    >
      <OhVueIcon :name="sendIcon" />
    </PButton>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from "vue";
import { PButton, timeout, useIcons, PTooltip } from "@pasta/box-v2";
import { OhVueIcon } from "oh-vue-icons";
import { RiRecordCircleFill, RiRecordCircleLine, RiSendPlane2Line, RiDeleteBin6Line } from "oh-vue-icons/icons";
import { useAudioRecorder } from "../composables/audio-recorder";
import AudioPlayerCustom from "./AudioPlayerCustom.vue";

interface Props {
  onData: (msg: { data: Blob; duration: number }) => any;
}
const props = defineProps<Props>();

const [recordIconFill, recordIconLine, sendIcon, deleteIcon] = useIcons(
  RiRecordCircleFill,
  RiRecordCircleLine,
  RiSendPlane2Line,
  RiDeleteBin6Line
);

const audioRecorder = useAudioRecorder();
const removeBtn = ref<typeof PButton | null>(null);
const playedTime = ref(0);

async function send() {
  const data = await audioRecorder.end();
  await props.onData(data);
  playerSrc.value = null;
}

function remove() {
  audioRecorder.cleanup();
  playerSrc.value = null;
}

async function pause() {
  audioRecorder.pause();
  await setupPlayback();
}

watch(
  () => audioRecorder.reachedMaxDuration.value,
  (newVal: boolean) => {
    if (newVal) setupPlayback();
  }
);

function resume() {
  audioRecorder.resume();
  playerSrc.value = null;
}

const canvas = ref<HTMLCanvasElement | null>(null);

let canvasCtx: CanvasRenderingContext2D | null = null;
const pulse = ref(false);
let timer: number;
onMounted(async () => {
  let nbTries = 0;
  while (!canvasCtx && nbTries < 10) {
    nbTries += 1;
    canvasCtx = canvas.value?.getContext("2d") || null;
    await timeout(10);
  }
  draw();

  timer = window.setInterval(() => (pulse.value = !pulse.value), 1_000);

  removeBtn.value?.mainElement.focus();
});

onUnmounted(() => window.clearInterval(timer));

function draw() {
  if (!canvas.value || !canvasCtx || !audioRecorder.analyser) return;
  requestAnimationFrame(draw);

  if (audioRecorder.state.value === "paused") return;

  const WIDTH = canvas.value.width;
  const HEIGHT = canvas.value.height;

  audioRecorder.analyser!.getByteTimeDomainData(audioRecorder.analyserDataArray);

  canvasCtx.fillStyle = "rgb(102, 102, 102)";
  canvasCtx.fillRect(0, 0, WIDTH, HEIGHT);

  canvasCtx.lineWidth = 2;
  canvasCtx.strokeStyle = "rgb(229, 229, 229)";

  canvasCtx.beginPath();

  const sliceWidth = (WIDTH * 1.0) / audioRecorder.analyserBufferLength;
  let x = 0;

  for (let i = 0; i < audioRecorder.analyserBufferLength; i++) {
    const v = audioRecorder.analyserDataArray[i] / 128.0;
    const y = (v * HEIGHT) / 2;

    if (i === 0) {
      canvasCtx.moveTo(x, y);
    } else {
      canvasCtx.lineTo(x, y);
    }

    x += sliceWidth;
  }

  canvasCtx.lineTo(WIDTH, HEIGHT / 2);
  canvasCtx.stroke();
}

// play recording
const playerSrc = ref<string | null>(null);
async function setupPlayback() {
  const blob = await audioRecorder.getCurrentData();
  playerSrc.value = URL.createObjectURL(blob);
}
</script>
